import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Sparkles, Loader2, CheckCircle, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Header from "../components/Header";
import ImageUploader from "../components/ImageUploader";
import AIListingGenerator from "../components/AIListingGenerator";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditListing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    price: "",
    description: "",
    floor: "",
    year_built: "",
    energy_certificate: "",
    features: [],
    images: []
  });
  
  const [newFeature, setNewFeature] = useState("");

  const availableFeatures = [
    "Parcare", "Balcon", "Terasă", "Centrală proprie", "Aer condiționat",
    "Mobilat", "Utilat", "Loc de joacă", "Pază", "Lift"
  ];

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      const data = response.data;
      setListing(data);
      setFormData({
        title: data.title || "",
        price: data.price || "",
        description: data.description || "",
        floor: data.floor || "",
        year_built: data.year_built || "",
        energy_certificate: data.energy_certificate || "",
        features: data.features || [],
        images: data.images || []
      });
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Eroare la încărcarea anunțului");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validare certificat energetic obligatoriu
    if (!formData.energy_certificate) {
      toast.error("Certificatul energetic este obligatoriu!");
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        price: parseFloat(formData.price),
        floor: formData.floor ? parseInt(formData.floor) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null
      };
      
      await axios.put(`${API}/listings/${id}`, updateData);
      toast.success("Anunț salvat cu succes!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Eroare la salvarea anunțului");
    } finally {
      setSaving(false);
    }
  };

  const generateAIDescription = async () => {
    if (!listing) return;
    
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/ai/description`, {
        property_type: listing.property_type,
        rooms: listing.rooms,
        surface: listing.surface,
        zone: listing.zone,
        city: listing.city,
        features: formData.features,
        price: parseFloat(formData.price) || listing.price
      });
      
      setFormData({ ...formData, description: response.data.description });
      toast.success("Descriere generată cu succes!");
    } catch (error) {
      console.error("Error generating AI description:", error);
      toast.error(error.response?.data?.detail || "Eroare la generarea descrierii");
    } finally {
      setGeneratingAI(false);
    }
  };

  const addFeature = (feature) => {
    if (!formData.features.includes(feature)) {
      setFormData({ ...formData, features: [...formData.features, feature] });
    }
  };

  const removeFeature = (feature) => {
    setFormData({
      ...formData,
      features: formData.features.filter(f => f !== feature)
    });
  };

  const addCustomFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature("");
    }
  };

  const handleImagesChange = (newImages) => {
    setFormData({ ...formData, images: newImages });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-stone-200 rounded w-1/3" />
            <div className="h-64 bg-stone-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-stone-700">Anunț negăsit</h2>
          <Link to="/dashboard">
            <Button className="mt-4">Înapoi la panou</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="text-stone-500 hover:text-stone-700 flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Înapoi la panou
        </Link>

        <div className="bg-white rounded-2xl p-6 sm:p-8 card-shadow">
          <h1 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-6">
            Editează anunțul
          </h1>

          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="manual">
                Editare manuală
              </TabsTrigger>
              <TabsTrigger value="ai">
                <Wand2 className="w-4 h-4 mr-2" />
                Generator AI
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ai">
              <AIListingGenerator 
                initialData={{
                  property_type: formData.property_type,
                  rooms: formData.rooms?.toString(),
                  surface: formData.surface?.toString(),
                  zone: formData.zone,
                  city: formData.city,
                  price: formData.price?.toString(),
                  currency: formData.currency
                }}
                onTitleGenerated={(title) => setFormData(prev => ({ ...prev, title }))}
                onDescriptionGenerated={(description) => setFormData(prev => ({ ...prev, description }))}
              />
            </TabsContent>

            <TabsContent value="manual">          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Titlu anunț</Label>
                <Input
                  id="title"
                  data-testid="edit-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="price">Preț (EUR)</Label>
                <Input
                  id="price"
                  data-testid="edit-price-input"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Property Details */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="floor">Etaj</Label>
                <Input
                  id="floor"
                  type="number"
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                  className="mt-2"
                  placeholder="ex: 3"
                />
              </div>
              <div>
                <Label htmlFor="year">An construcție</Label>
                <Input
                  id="year"
                  type="number"
                  value={formData.year_built}
                  onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                  className="mt-2"
                  placeholder="ex: 2020"
                />
              </div>
              <div>
                <Label htmlFor="energy" className="flex items-center gap-1">
                  Certificat energetic <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.energy_certificate}
                  onValueChange={(v) => setFormData({ ...formData, energy_certificate: v })}
                >
                  <SelectTrigger data-testid="energy-certificate-select" className="mt-2">
                    <SelectValue placeholder="Obligatoriu" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-green-500 text-white rounded flex items-center justify-center text-xs font-bold">A</span>
                        Clasa A (Casă verde)
                      </span>
                    </SelectItem>
                    <SelectItem value="B">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-green-400 text-white rounded flex items-center justify-center text-xs font-bold">B</span>
                        Clasa B (Casă verde)
                      </span>
                    </SelectItem>
                    <SelectItem value="C">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-yellow-400 text-black rounded flex items-center justify-center text-xs font-bold">C</span>
                        Clasa C
                      </span>
                    </SelectItem>
                    <SelectItem value="D">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-yellow-500 text-black rounded flex items-center justify-center text-xs font-bold">D</span>
                        Clasa D
                      </span>
                    </SelectItem>
                    <SelectItem value="E">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-orange-400 text-white rounded flex items-center justify-center text-xs font-bold">E</span>
                        Clasa E
                      </span>
                    </SelectItem>
                    <SelectItem value="F">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-orange-500 text-white rounded flex items-center justify-center text-xs font-bold">F</span>
                        Clasa F
                      </span>
                    </SelectItem>
                    <SelectItem value="G">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-red-500 text-white rounded flex items-center justify-center text-xs font-bold">G</span>
                        Clasa G
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description with AI */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="description">Descriere</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateAIDescription}
                  disabled={generatingAI}
                  data-testid="generate-ai-button"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  {generatingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Se generează...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      Generează cu AI
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description"
                data-testid="edit-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                placeholder="Descrie proprietatea ta..."
              />
            </div>

            {/* Features */}
            <div>
              <Label>Facilități</Label>
              <div className="flex flex-wrap gap-2 mt-3 mb-3">
                {availableFeatures.map((feature) => (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => 
                      formData.features.includes(feature) 
                        ? removeFeature(feature) 
                        : addFeature(feature)
                    }
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      formData.features.includes(feature)
                        ? "bg-emerald-100 text-emerald-800 border-2 border-emerald-300"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {formData.features.includes(feature) && (
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                    )}
                    {feature}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Adaugă altă facilitate..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addCustomFeature()}
                />
                <Button type="button" variant="outline" onClick={addCustomFeature}>
                  Adaugă
                </Button>
              </div>

              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.features.map((feature) => (
                    <Badge key={feature} variant="secondary" className="pl-2 pr-1 py-1">
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="ml-1 hover:bg-stone-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Images */}
            <div>
              <Label className="mb-3 block">Imagini proprietate</Label>
              <ImageUploader 
                images={formData.images}
                onImagesChange={handleImagesChange}
                maxImages={10}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-stone-200">
              <Button
                data-testid="save-listing-button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-emerald-950 hover:bg-emerald-900 h-12 rounded-xl"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvează modificările
                  </>
                )}
              </Button>
              <Link to="/dashboard">
                <Button variant="outline" className="h-12 rounded-xl">
                  Anulează
                </Button>
              </Link>
            </div>
          </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default EditListing;
